import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListHsnCodes,
  useCreateHsnCode,
  useUpdateHsnCode,
  useDeleteHsnCode,
  getListHsnCodesQueryKey,
} from "@workspace/api-client-react";
import type { InventoryHsnCode } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { ListPagination } from "@/components/common/list-pagination";

// Standard Indian GST slabs (40% added 2026-05 for aerated/sugary drinks).
const GST_RATES = [0, 5, 12, 18, 28, 40] as const;

interface Props {
  openCreate: boolean;
  setOpenCreate: (open: boolean) => void;
}

interface FormState {
  code: string;
  codeType: "HSN" | "SAC";
  description: string;
  gstRate: number;
  status: "Active" | "Archived";
}

const EMPTY: FormState = { code: "", codeType: "HSN", description: "", gstRate: 18, status: "Active" };

export function HsnCodesTab({ openCreate, setOpenCreate }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [editRow, setEditRow] = useState<InventoryHsnCode | null>(null);
  const [deleteRow, setDeleteRow] = useState<InventoryHsnCode | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);

  const { data: codes, isLoading } = useListHsnCodes();
  const createMutation = useCreateHsnCode();
  const updateMutation = useUpdateHsnCode();
  const deleteMutation = useDeleteHsnCode();

  const sheetOpen = openCreate || editRow !== null;

  // Pre-fill the form when opening for create vs edit.
  useEffect(() => {
    if (editRow) {
      setForm({
        code: editRow.code,
        codeType: (editRow.codeType as "HSN" | "SAC") ?? "HSN",
        description: editRow.description ?? "",
        gstRate: Number(editRow.gstRate),
        status: (editRow.status as "Active" | "Archived") ?? "Active",
      });
    } else if (openCreate) {
      setForm(EMPTY);
    }
  }, [editRow, openCreate]);

  const q = search.trim().toLowerCase();
  const filtered = (codes ?? []).filter(
    (c) => !q || c.code.toLowerCase().includes(q) || (c.description ?? "").toLowerCase().includes(q),
  );
  // Client-side pagination: this endpoint stays a bare array so the HSN combobox
  // keeps getting the full list. The browse table paginates the filtered result.
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => {
    setPage(1);
  }, [search, pageSize]);

  function closeSheet() {
    setOpenCreate(false);
    setEditRow(null);
    setForm(EMPTY);
  }

  async function handleSave() {
    if (!form.code.trim()) {
      toast({ title: "Code is required.", variant: "destructive" });
      return;
    }
    try {
      if (editRow) {
        await updateMutation.mutateAsync({
          id: editRow.id,
          data: {
            code: form.code.trim(),
            codeType: form.codeType,
            description: form.description || null,
            gstRate: form.gstRate,
            status: form.status,
          },
        });
        toast({ title: "HSN code updated." });
      } else {
        await createMutation.mutateAsync({
          data: {
            code: form.code.trim(),
            codeType: form.codeType,
            description: form.description || null,
            gstRate: form.gstRate,
            status: form.status,
          },
        });
        toast({ title: "HSN code added." });
      }
      queryClient.invalidateQueries({ queryKey: getListHsnCodesQueryKey() });
      closeSheet();
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      toast({
        title: status === 409 ? "That code already exists in this space." : "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteRow) return;
    try {
      await deleteMutation.mutateAsync({ id: deleteRow.id });
      toast({ title: `HSN ${deleteRow.code} deleted.` });
      queryClient.invalidateQueries({ queryKey: getListHsnCodesQueryKey() });
      setDeleteRow(null);
    } catch {
      toast({ title: "Could not delete. Please try again.", variant: "destructive" });
      setDeleteRow(null);
    }
  }

  return (
    <>
      <div className="mb-4 flex gap-2 items-center flex-wrap">
        <Input
          placeholder="Search code or description…"
          className="w-full sm:w-[280px]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <p className="text-xs text-muted-foreground ml-auto">
          Rates are seeded defaults — verify against your filing.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">Code</TableHead>
                <TableHead className="w-[80px]">Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[90px] text-right">GST %</TableHead>
                <TableHead className="w-[90px]">Status</TableHead>
                <TableHead className="w-[140px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(6)].map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    No HSN/SAC codes yet. Click “Add HSN Code”.
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-mono text-sm">{c.code}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.codeType}</TableCell>
                    <TableCell className="text-sm">{c.description ?? "—"}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{Number(c.gstRate)}%</TableCell>
                    <TableCell>
                      {c.status === "Archived" ? (
                        <Badge variant="outline" className="text-muted-foreground">Archived</Badge>
                      ) : (
                        <Badge variant="secondary">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => { setOpenCreate(false); setEditRow(c); }}>Edit</Button>
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setDeleteRow(c)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {!isLoading && filtered.length > 0 ? (
            <ListPagination
              total={filtered.length}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              isLoading={isLoading}
              noun={["code", "codes"]}
            />
          ) : null}
        </CardContent>
      </Card>

      <Sheet open={sheetOpen} onOpenChange={(o) => !o && closeSheet()}>
        <SheetContent side="right" className="w-[440px] flex flex-col">
          <SheetHeader>
            <SheetTitle>{editRow ? `Edit HSN ${editRow.code}` : "Add HSN Code"}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 space-y-4 py-4">
            <div>
              <label className="text-xs font-semibold">Code <span className="text-destructive">*</span></label>
              <Input
                placeholder="e.g. 2202"
                value={form.code}
                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                className="font-mono"
              />
            </div>
            <div>
              <label className="text-xs font-semibold">Type</label>
              <Select value={form.codeType} onValueChange={(v) => setForm((p) => ({ ...p, codeType: v as "HSN" | "SAC" }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="HSN">HSN (goods)</SelectItem>
                  <SelectItem value="SAC">SAC (services)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold">Description</label>
              <Input
                placeholder="e.g. Aerated / sugary drinks"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs font-semibold">GST % <span className="text-destructive">*</span></label>
              <Select value={String(form.gstRate)} onValueChange={(v) => setForm((p) => ({ ...p, gstRate: Number(v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v as "Active" | "Archived" }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={closeSheet}>Cancel</Button>
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>
              {createMutation.isPending || updateMutation.isPending ? "Saving…" : (editRow ? "Save Changes" : "Add Code")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteRow !== null} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete HSN {deleteRow?.code}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the code from the GST lookup. Items already using this code keep their saved
              HSN/GST — they just won&rsquo;t auto-fill from it anymore. This can&rsquo;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
